import { z } from "zod";

import { RequirementKeySchema } from "./progress";

const LabelSchema = z.string().trim().min(1).max(200);
const visibleToAll = { visibleTo: "*" } as const;

const TaskFlowLinkSchema = z
  .object({
    to: z
      .string()
      .regex(/^\/[A-Za-z0-9/$_-]*$/)
      .max(200)
      .describe("Route template inside the task's context, e.g. /cases/$caseNumber")
      .meta(visibleToAll),
    search: z
      .record(z.string().min(1).max(50), z.string().min(1).max(100))
      .optional()
      .describe("Search parameters the route receives, e.g. { tab: 'contacts' }")
      .meta(visibleToAll),
  })
  .strict()
  .describe("A page of the task's case or client where the assignee does the item");
export type TaskFlowLink = z.infer<typeof TaskFlowLinkSchema>;

export const ChecklistItemSchema = z
  .object({
    id: RequirementKeySchema.describe(
      "Stable key the task's checklist answers are stored under; never reused",
    ).meta(visibleToAll),
    label: LabelSchema.describe("What the assignee confirms, shown as the row label").meta(
      visibleToAll,
    ),
    required: z
      .boolean()
      .describe("Whether completion waits for this item to be ticked")
      .meta(visibleToAll),
    resolutionIds: z
      .array(RequirementKeySchema)
      .describe("Resolutions the item applies to; empty means every resolution")
      .meta(visibleToAll),
    groupId: RequirementKeySchema.optional()
      .describe("Group whose minimum this item counts towards")
      .meta(visibleToAll),
    link: TaskFlowLinkSchema.optional().meta(visibleToAll),
  })
  .strict()
  .describe("One manual checklist tick configured for a task type");
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

export const ChecklistGroupSchema = z
  .object({
    id: RequirementKeySchema.describe(
      "Stable group key, reported when the minimum is not met",
    ).meta(visibleToAll),
    label: LabelSchema.describe("Heading shown above the group's items").meta(visibleToAll),
    minimumSatisfied: z
      .number()
      .int()
      .positive()
      .describe("How many applicable items of the group must be ticked")
      .meta(visibleToAll),
  })
  .strict()
  .describe("Items of which at least a minimum must be ticked");
export type ChecklistGroup = z.infer<typeof ChecklistGroupSchema>;

/** The items that apply once a resolution is chosen; only common items before that. */
export const resolutionChecklist = (
  checklist: readonly ChecklistItem[],
  resolutionId: string | null | undefined,
): ChecklistItem[] =>
  checklist.filter(
    ({ resolutionIds }) =>
      resolutionIds.length === 0 || (resolutionId != null && resolutionIds.includes(resolutionId)),
  );
